import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { fetchKodaInsights } from 'api/dashboardKodaInsights'
import { kodaInsightsStore } from 'shared/stores/kodaInsightsStore'
import { useTasksPolling } from 'shared/hooks/useTasksPolling'
import SendMessageModal from 'pages/Students/components/SendMessageModal/SendMessageModal'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import KodaInsightCard from './components/KodaInsightCard'
import s from './styles/KodaInsightsSection.module.scss'

const ACTION_BULK_REMINDER = 'open_bulk_reminder'
const ACTION_ASSIGNMENT_MODAL = 'open_assignment_modal'

const toModalStudent = student => {
	const id = String(student?.studentId || '').trim()
	const name = student?.studentName || 'Student'
	return {
		id,
		studentId: id,
		name,
		username: name,
	}
}

const KodaInsightsSection = observer(() => {
	const [activeInsight, setActiveInsight] = useState(null)
	const [sendMessageOpen, setSendMessageOpen] = useState(false)
	const [assignOpen, setAssignOpen] = useState(false)

	const loadInsights = useCallback(async () => {
		try {
			kodaInsightsStore.beginLoad()
			const res = await fetchKodaInsights()
			const items = Array.isArray(res?.data?.insights)
				? res.data.insights
				: []
			kodaInsightsStore.setInsights(items)
			return items.map(item => ({
				id: item.id,
				student_status: item.count,
			}))
		} catch (err) {
			console.error('Failed to load Koda insights', err)
			kodaInsightsStore.setError()
			return []
		}
	}, [])

	useEffect(() => {
		loadInsights()
	}, [loadInsights])

	useTasksPolling({
		onUpdate: loadInsights,
		enabled: true,
		initialInterval: 60000,
		maxInterval: 300000,
		backoffMultiplier: 1.5,
	})

	const openInsightAction = useCallback(insight => {
		if (!insight?.actionType) return
		setActiveInsight(insight)
		if (insight.actionType === ACTION_BULK_REMINDER) {
			setSendMessageOpen(true)
		} else if (insight.actionType === ACTION_ASSIGNMENT_MODAL) {
			setAssignOpen(true)
		}
	}, [])

	// Temporary demo-safe behavior: do NOT dismiss the insight after CTA success.
	// The demo must stay repeatable — Navdeep may click the CTA multiple times
	// during the presentation. Polling still refreshes from the backend.
	const handleCtaSuccess = useCallback(() => {
		setActiveInsight(null)
	}, [])

	const closeAssign = useCallback(() => {
		setAssignOpen(false)
		handleCtaSuccess()
	}, [handleCtaSuccess])

	const insights = kodaInsightsStore.insights
	if (
		insights.length === 0 &&
		kodaInsightsStore.loadStatus !== 'loading'
	) {
		return null
	}

	const payloadStudents = Array.isArray(activeInsight?.payload?.students)
		? activeInsight.payload.students
		: []
	const selectedStudents = payloadStudents.map(toModalStudent).filter(s => s.id)
	const prefillMessage = activeInsight?.payload?.prefillMessage || ''
	const assignmentType = activeInsight?.payload?.assignmentType || ''
	const essayScopeFromPayload = activeInsight?.payload?.essayScope || ''

	return (
		<section className={s.section}>
			<header className={s.header}>
				<div className={s.titleRow}>
					<h2 className={s.title}>Koda AI Insights</h2>
					<span className={s.smartBadge}>SMART</span>
				</div>
				<p className={s.subtitle}>
					AI-powered recommendations to speed up counselor workflows.
				</p>
			</header>

			<div className={s.grid}>
				{insights.map(insight => (
					<KodaInsightCard
						key={insight.id}
						insight={insight}
						onAction={() => openInsightAction(insight)}
					/>
				))}
			</div>

			<SendMessageModal
				isOpen={sendMessageOpen}
				onClose={() => setSendMessageOpen(false)}
				selectedStudents={selectedStudents}
				initialMessage={prefillMessage}
				onAfterSendSuccess={handleCtaSuccess}
			/>

			<AssignActivityModal
				isOpen={assignOpen}
				onClose={() => setAssignOpen(false)}
				onSuccess={closeAssign}
				selectedStudents={selectedStudents}
				initialAssignmentType={assignmentType || 'Essay'}
				initialEssayMode={essayScopeFromPayload}
				clearSelectionOnOpen={false}
			/>
		</section>
	)
})

export default KodaInsightsSection
